import _omit from "lodash/omit";
import { buildMultiValueKeyFilter } from 'app/forms/config/suppressions/suppression';
import { getSuppressionReferenceIds } from '@kentik/ui-shared/alerting/suppressionReferenceIds';
import { unknownDimensionLabel } from './suppressionDimensions';

/**
 * $syn and $alerting are untyped JavaScript stores with far broader surfaces than suppressions
 * use, so these describe only the slice consumed here rather than the stores as a whole.
 * LoadState distinguishes "still loading" from "genuinely missing".
 */

/** The part of the alerting store suppressions read: policy lookup by rule ID. */

/** The part of the synthetics store suppressions read: test and agent lookup by ID. */

const REFERENCE_LABELS = {
  test: {
    one: 'test',
    many: 'tests'
  },
  agent: {
    one: 'agent',
    many: 'agents'
  },
  policy: {
    one: 'policy',
    many: 'policies'
  },
  dimension: {
    one: 'dimension',
    many: 'dimensions'
  }
};

/** Shown in place of a resource the user can no longer see. Never includes the raw ID. */
export function unavailableReferenceLabel(type) {
  return `Unavailable ${REFERENCE_LABELS[type].one}`;
}

/** Collapses references into one line per type, so several unavailable resources read sensibly. */
export function unavailableReferenceSummary(references) {
  const types = Object.keys(REFERENCE_LABELS);
  return types.flatMap(type => {
    const ofType = references.filter(reference => reference.type === type);
    const count = ofType.length;
    if (!count) {
      return [];
    }
    if (type === 'dimension') {
      return [`Unknown ${REFERENCE_LABELS[type][count === 1 ? 'one' : 'many']}: ${ofType.map(({
        id
      }) => id).join(', ')}`];
    }
    return [count === 1 ? unavailableReferenceLabel(type) : `${count} unavailable ${REFERENCE_LABELS[type].many}`];
  });
}
export function unknownDimensionReferences(keys) {
  return keys.map(key => ({
    type: 'dimension',
    id: key,
    label: unknownDimensionLabel(key)
  }));
}
/**
 * The IDs a single clause contributes to the selector. Empty means the clause is not owned by
 * the editor -- it is keyed on something else, or it is a negated predicate the UI cannot express.
 */
function referenceIds(filter, keys) {
  return getSuppressionReferenceIds({
    filters: [filter]
  }, keys);
}
export function stripOldKeyFilter(keyFilter) {
  return {
    ..._omit(keyFilter, 'filter'),
    filters: keyFilter?.filters ? [...keyFilter.filters] : []
  };
}
export function updateReferenceKeyFilter(existing, field, selectedIds) {
  const keys = Array.isArray(field) ? field : [field];
  const previous = getSuppressionReferenceIds(existing, keys);
  if (existing && previous.length === selectedIds.length && previous.every(id => selectedIds.includes(id))) {
    return stripOldKeyFilter(existing);
  }
  const filters = existing?.filters || [];
  const [ownedClause] = filters.filter(filter => referenceIds(filter, keys).length > 0);
  const selected = selectedIds.length ? buildMultiValueKeyFilter(field, selectedIds).filters : [];
  if (!ownedClause) {
    return {
      ..._omit(existing, 'filter'),
      filters: [...filters, ...selected]
    };
  }
  return {
    ..._omit(existing, 'filter'),
    filters: filters.flatMap(filter => filter === ownedClause ? selected : [filter])
  };
}
export function getUnavailableReferences({
  ruleIds = [],
  testIds = [],
  agentIds = []
}, {
  $syn,
  $alerting
}) {
  const references = [];
  const append = (type, ids, collection, resolve) => {
    if (!collection?.hasFetched || collection.requestStatus || collection.error) {
      return;
    }
    ids.forEach(id => {
      if (!resolve(id)) {
        references.push({
          type,
          id,
          label: unavailableReferenceLabel(type)
        });
      }
    });
  };
  if (ruleIds.length) {
    append('policy', ruleIds, $alerting.policyCollection, id => $alerting.policyCollection.getPolicyByRuleId(id));
  }
  if (testIds.length) {
    append('test', testIds, $syn.tests, id => $syn.tests?.get(id));
  }
  if (agentIds.length) {
    append('agent', agentIds, $syn.agents, id => $syn.agents?.get(id));
  }
  return references;
}